import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { formatValue, niceScale } from '@/components/charts/chart-utils'

interface BarChartProps {
    title: string
    labels: Array<string>
    values: Array<number | null>
    unit?: string
    className?: string
}

/**
 * Horizontal thin bars from a single baseline, each labelled at its tip.
 */
export const BarChart: FC<BarChartProps> = ({ title, labels, values, unit, className }): ReactNode => {
    const finite = values.filter((value): value is number => value !== null)
    const scale = niceScale(finite, 4)
    const max = scale.max || 1

    return (
        <div className={cn('w-full', className)} role='group' aria-label={title}>
            <ul className='space-y-4'>
                {labels.map((label, index) => {
                    const value = values[index] ?? null
                    const ratio = value === null ? 0 : Math.max(0, Math.min(1, value / max))

                    return (
                        <li key={`${label}-${index}`} className='group'>
                            <div className='flex items-baseline justify-between gap-4'>
                                <span className='hud text-mute transition-colors duration-300 group-hover:text-ink'>
                                    {label}
                                </span>
                            </div>

                            <div className='mt-2 flex items-center gap-3'>
                                <div className='relative h-2.5 flex-1 border-l border-[#3A362F]'>
                                    <div
                                        className='absolute inset-y-0 left-0 bg-point/85 transition-colors duration-300 group-hover:bg-ink'
                                        style={{ width: `${ratio * 100}%` }}
                                    />
                                </div>

                                <span className='hud w-20 shrink-0 text-right text-ink'>
                                    {formatValue(value, unit)}
                                </span>
                            </div>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
